import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import notifications from "../../../../common/Components/Notifications/Notification";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { isKarkhanaSupervisor } from "../../../../helpers/karkhanaEnabled";
import { getCashDay, listLedgerHeads, saveExpense } from "../../../../services/cashBookApi";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { rupees } from "../../rates/rateModel";
import PageHeader from "../../ui/PageHeader";
import CashbookGuard, { readRole } from "../CashbookGuard";
import { istToday, num, refusalText } from "../money";
import { sendOne } from "../photoQueue";
import type { LedgerHead } from "../types";
import Keypad from "../ui/Keypad";
import { preparePhoto } from "./cameraTools";
import "../cashbook.scss";

/** The heads shown as chips: the ones he used lately first, then the rest, five in all. */
export const chipHeads = (heads: LedgerHead[], n = 5): LedgerHead[] => {
  const active = heads.filter((h) => h.active);
  return [...active.filter((h) => h.recent), ...active.filter((h) => !h.recent)].slice(0, n);
};

export const headLabel = (h: LedgerHead, lang: "en" | "hi") => (lang === "hi" && h.nameHi ? h.nameHi : h.name);

/**
 * खर्च लिखें: one petty cash spend (V1_SPEC 11.4 A7/A8, 7.8). Amount on the keypad, what it was for
 * (recent spend types as chips), optional payee, work and bill photo. Spending more than the cash in
 * hand is allowed with a note (D22). Staff may open it for a supervisor with `?holder=`.
 */
const SpendScreen = () => {
  const { t, lang } = useKT();
  const history = useHistory();
  const { search } = useLocation();
  const status = useCashbookStatus();
  const q = new URLSearchParams(search);
  const supervisor = isKarkhanaSupervisor(readRole());
  const holderUserId = supervisor ? null : q.get("holder");
  const [heads, setHeads] = useState<LedgerHead[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [inHand, setInHand] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const [headId, setHeadId] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [find, setFind] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [note, setNote] = useState("");
  const [bill, setBill] = useState<{ id: string } | null>(null);
  const [billBusy, setBillBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // One id for this spend: a second tap or a retry after a lost answer saves it once.
  const [requestId] = useState(newRequestId);
  const billInput = useRef<HTMLInputElement>(null);

  const load = () => {
    setLoadError(false);
    listLedgerHeads(false)
      .then((res) => (res?.success ? setHeads(res.data?.heads || []) : setLoadError(true)))
      .catch(() => setLoadError(true));
    if (!holderUserId || supervisor)
      getCashDay(istToday(), holderUserId || undefined)
        .then((res) => res?.success && setInHand(res.data?.cash?.inHand ?? null))
        .catch(() => undefined);
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const chips = useMemo(() => chipHeads(heads || []), [heads]);
  const found = useMemo(() => {
    const s = find.trim().toLowerCase();
    const all = (heads || []).filter((h) => h.active);
    return s ? all.filter((h) => h.name.toLowerCase().includes(s) || (h.nameHi || "").includes(find.trim())) : all;
  }, [heads, find]);
  const chosen = (heads || []).find((h) => h.id === headId) || null;

  const addBill = async (file: File) => {
    setBillBusy(true);
    setError(null);
    try {
      let blob: Blob = file;
      try {
        blob = (await preparePhoto(file)).blob;
      } catch {
        // Send the phone's own file when the browser can't shrink it.
      }
      const out = await sendOne({
        clientRequestId: newRequestId(),
        blob,
        contentType: blob.type || "image/jpeg",
        fileType: /png/i.test(blob.type) ? "PNG" : /hei/i.test(blob.type) ? "HEIC" : "JPG",
        createdAt: Date.now(),
        meta: { kind: "BILL", date: istToday(), printId: null, page: null, qrText: null, handwritten: false, declaredTotal: null, blurryAccepted: false, holderUserId },
      });
      if (out.state === "sent") setBill({ id: out.response.photo.id });
      else if (out.state === "alreadySent" && out.photoId) setBill({ id: out.photoId });
      else if (out.state === "refused") setError(refusalText(out, t));
      else setError(t("No network. Nothing changed; tap again."));
    } finally {
      setBillBusy(false);
    }
  };

  const save = async () => {
    if (num(amount) <= 0) return setError(t("Type the amount."));
    if (!headId) return setError(t("Choose a spend type."));
    setBusy(true);
    setError(null);
    try {
      const res = await saveExpense({
        amount,
        ledgerHeadId: headId,
        paidTo: paidTo.trim() || null,
        note: note.trim() || null,
        date: istToday(),
        billPhotoId: bill?.id ?? null,
        spendsPhotoId: null,
        holderUserId,
        clientRequestId: requestId,
      });
      if (!res?.success) {
        setError(refusalText(res, t));
        return;
      }
      notifications({ title: t("Spend saved · Cash in hand {{amount}}", { amount: rupees(res.data?.cashInHand) }), message: "", type: "success" });
      status.refresh();
      history.push(holderUserId ? kp.cashHolder(holderUserId) : kp.cashBook());
    } catch {
      setError(t("No network. Nothing changed; tap again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cb">
      <PageHeader title={t("Type a spend")} back={kp.cashBook()} backLabel={t("Back")} />
      <Keypad value={amount} onChange={setAmount} label={t("Amount")} id="cb-spend-amount" />
      {inHand !== null && num(amount) > num(inHand) ? (
        <p className="cb-muted cb-state is-warn" aria-live="polite">
          {t("You seem to have only {{amount}}", { amount: rupees(inHand) })}
        </p>
      ) : null}

      <div className="cb-field">
        <span className="cb-field__label" id="cb-head-label">
          {t("What for?")}
        </span>
        {loadError ? (
          <div className="cb-error" role="alert">
            <span>{t("Couldn't load. Check the network.")}</span>
            <button type="button" className="kh-btn" onClick={load}>
              {t("Retry")}
            </button>
          </div>
        ) : heads === null ? (
          <div className="cb-skeleton" />
        ) : (
          <div className="cb-chips" role="group" aria-labelledby="cb-head-label">
            {(searching ? found : chips).map((h) => (
              <button key={h.id} type="button" className={`cb-chip${headId === h.id ? " is-on" : ""}`} aria-pressed={headId === h.id} onClick={() => setHeadId(h.id)}>
                {headLabel(h, lang)}
              </button>
            ))}
            {chosen && !(searching ? found : chips).some((h) => h.id === chosen.id) ? (
              <button type="button" className="cb-chip is-on" aria-pressed>
                {headLabel(chosen, lang)}
              </button>
            ) : null}
            {!searching ? (
              <button type="button" className="cb-chip" onClick={() => setSearching(true)}>
                {t("Search")}
              </button>
            ) : null}
          </div>
        )}
        {searching ? (
          <input value={find} onChange={(e) => setFind(e.target.value)} placeholder={t("Search")} aria-label={t("Search")} autoFocus />
        ) : null}
      </div>

      <label className="cb-field">
        <span className="cb-field__label">{t("Paid to (optional)")}</span>
        <input value={paidTo} onChange={(e) => setPaidTo(e.target.value)} maxLength={80} autoComplete="off" />
      </label>
      <label className="cb-field">
        <span className="cb-field__label">{t("For what work? (optional)")}</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} autoComplete="off" />
      </label>

      <input
        ref={billInput}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: "none" }}
        data-testid="cb-bill"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) addBill(f);
        }}
      />
      <button type="button" className="cb-btn is-small" disabled={billBusy} onClick={() => billInput.current?.click()}>
        {billBusy ? t("Sending…") : bill ? t("Bill photo added ✓") : t("Bill photo (optional)")}
      </button>

      {error ? (
        <div className="cb-error" role="alert">
          <span>{error}</span>
        </div>
      ) : null}

      <button type="button" className="cb-btn is-primary" disabled={busy || billBusy} onClick={save}>
        {busy ? t("Saving…") : t("Save {{amount}} spend", { amount: rupees(amount || 0) })}
      </button>
    </div>
  );
};

const SpendForm = () => (
  <CashbookGuard>
    <SpendScreen />
  </CashbookGuard>
);

export default SpendForm;
